import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import cobrancaListagensService from "../services/cobrancaListagens.service";
import cobrancaNotificacoesService from "../services/cobrancaNotificacoes.service";
import { formatCurrency, formatDays, formatNumber } from "../utils/formatters";

const EMPTY_TITULOS_FILTERS = {
  companyId: "",
  clientId: "",
  receivableBillId: "",
  clientName: "",
  documentNumber: "",
  costCenterId: "",
};

const EMPTY_PARCELAS_FILTERS = {
  companyId: "",
  receivableBillId: "",
  installmentId: "",
  clientId: "",
  clientName: "",
  documentNumber: "",
  conditionType: "",
  costCenterId: "",
  dueDateInicio: "",
  dueDateFim: "",
  diasAtrasoMin: "",
  diasAtrasoMax: "",
};

const TITULOS_FIELDS = [
  { name: "clientName", label: "Cliente" },
  { name: "documentNumber", label: "Documento" },
  { name: "companyId", label: "Empresa (Sienge)", numeric: true },
  { name: "clientId", label: "Código do cliente", numeric: true },
  { name: "receivableBillId", label: "Código do título", numeric: true },
  { name: "costCenterId", label: "Centro de custo" },
];

const PARCELAS_FIELDS = [
  { name: "clientName", label: "Cliente" },
  { name: "documentNumber", label: "Documento" },
  { name: "conditionType", label: "Condition type" },
  { name: "costCenterId", label: "Centro de custo" },
  { name: "companyId", label: "Empresa (Sienge)", numeric: true },
  { name: "clientId", label: "Código do cliente", numeric: true },
  { name: "receivableBillId", label: "Código do título", numeric: true },
  { name: "installmentId", label: "Código da parcela", numeric: true },
  { name: "dueDateInicio", label: "Vencimento a partir de", type: "date" },
  { name: "dueDateFim", label: "Vencimento até", type: "date" },
  { name: "diasAtrasoMin", label: "Atraso mínimo (dias)", numeric: true },
  { name: "diasAtrasoMax", label: "Atraso máximo (dias)", numeric: true },
];

const EMPTY_PAGE = { content: [], totalElements: 0, totalPages: 0 };

const formatDate = (value) => {
  if (!value) return "—";
  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) return `${dateOnlyMatch[3]}/${dateOnlyMatch[2]}/${dateOnlyMatch[1]}`;

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
};

const formatNullableCurrency = (value) => value === null || value === undefined ? "—" : formatCurrency(value);
const formatNullableNumber = (value) => value === null || value === undefined ? "—" : formatNumber(value, { maximumFractionDigits: 1 });

function FilterFields({ fields, values, onChange, centrosCusto, centrosCustoLoading, centrosCustoError }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }, gap: 1.25 }}>
      {fields.map((field) => field.name === "costCenterId" ? (
        <TextField
          key={field.name}
          select
          label={field.label}
          value={values[field.name]}
          onChange={(event) => onChange(field.name, event.target.value)}
          helperText={centrosCustoError || (centrosCustoLoading ? "Carregando centros de custo..." : "Opcional")}
          size="small"
          fullWidth
          disabled={centrosCustoLoading || Boolean(centrosCustoError)}
        >
          <MenuItem value="">Todos os centros de custo</MenuItem>
          {centrosCusto.map((centroCusto) => (
            <MenuItem key={`${centroCusto.companyId}-${centroCusto.costCenterId}`} value={centroCusto.costCenterId}>
              {centroCusto.nome || "Centro sem nome"} ({centroCusto.costCenterId})
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField
          key={field.name}
          label={field.label}
          type={field.type || (field.numeric ? "number" : "text")}
          value={values[field.name]}
          onChange={(event) => onChange(field.name, event.target.value)}
          size="small"
          fullWidth
          InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
          inputProps={field.numeric ? { min: 0, step: 1 } : undefined}
        />
      ))}
    </Box>
  );
}

function ListPagination({ page, pageSize, totalElements, loading, onPageChange, onPageSizeChange }) {
  return (
    <TablePagination
      component="div"
      count={totalElements}
      page={page}
      rowsPerPage={pageSize}
      rowsPerPageOptions={[10, 25, 50, 100]}
      onPageChange={onPageChange}
      onRowsPerPageChange={onPageSizeChange}
      labelRowsPerPage="Linhas por página:"
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      sx={{ borderTop: "1px solid var(--ops-border)", color: "var(--ops-muted)", opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto", "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12 } }}
    />
  );
}

function ParcelDetail({ parcel, onClose }) {
  if (!parcel) return null;

  const fields = [
    ["Cliente", parcel.clientName],
    ["Código do cliente", parcel.clientId],
    ["Documento", parcel.documentNumber],
    ["Empresa", parcel.companyId],
    ["Código do título", parcel.receivableBillId],
    ["Código da parcela", parcel.installmentId],
    ["Parcela", parcel.installmentNumber],
    ["Condition type", parcel.conditionType],
    ["Vencimento", formatDate(parcel.dueDate)],
    ["Dias em atraso", parcel.daysOfDelay === null || parcel.daysOfDelay === undefined ? "—" : formatDays(parcel.daysOfDelay)],
    ["Valor sem acréscimos", formatNullableCurrency(parcel.correctedValueWithoutAdditions)],
    ["Pro rata", formatNullableCurrency(parcel.proRata)],
    ["Juros", formatNullableCurrency(parcel.interest)],
    ["Multa", formatNullableCurrency(parcel.fine)],
    ["Total de acréscimos", formatNullableCurrency(parcel.totalAdditions)],
    ["Valor corrigido", formatNullableCurrency(parcel.correctedValueWithAdditions)],
    ["Enviada ao SPC/Serasa", parcel.installmentSentToSPCSerasa ? "Sim" : "Não"],
  ];

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: "var(--ops-heading)", fontWeight: 800, pr: 7 }}>
        Detalhes da parcela
        <Button onClick={onClose} aria-label="Fechar detalhes" sx={{ position: "absolute", right: 12, top: 10, minWidth: 36, color: "var(--ops-muted)" }}>
          <CloseRounded />
        </Button>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "var(--ops-border)" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5, py: 0.5 }}>
          {fields.map(([label, value]) => (
            <Box key={label}>
              <Typography sx={{ color: "var(--ops-muted)", fontSize: 12 }}>{label}</Typography>
              <Typography sx={{ color: "var(--ops-heading)", fontSize: 14, fontWeight: 650, overflowWrap: "anywhere" }}>{value ?? "—"}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CobrancaTitulosPage() {
  const [view, setView] = useState("titulos");
  const [draftFilters, setDraftFilters] = useState(EMPTY_TITULOS_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_TITULOS_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasLoadedResponse, setHasLoadedResponse] = useState(false);
  const [error, setError] = useState("");
  const [canRetry, setCanRetry] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [centrosCustoLoading, setCentrosCustoLoading] = useState(true);
  const [centrosCustoError, setCentrosCustoError] = useState("");
  const viewCache = useRef({
    titulos: {
      draftFilters: EMPTY_TITULOS_FILTERS,
      appliedFilters: EMPTY_TITULOS_FILTERS,
      page: 0,
      pageSize: 25,
      result: EMPTY_PAGE,
      hasSearched: false,
      hasLoadedResponse: false,
      error: "",
      canRetry: false,
    },
    parcelas: {
      draftFilters: EMPTY_PARCELAS_FILTERS,
      appliedFilters: EMPTY_PARCELAS_FILTERS,
      page: 0,
      pageSize: 25,
      result: EMPTY_PAGE,
      hasSearched: false,
      hasLoadedResponse: false,
      error: "",
      canRetry: false,
    },
  });
  const skipFetchForView = useRef(null);

  const isParcelasView = view === "parcelas";
  const fields = useMemo(() => isParcelasView ? PARCELAS_FIELDS : TITULOS_FIELDS, [isParcelasView]);

  useEffect(() => {
    let active = true;
    cobrancaNotificacoesService.getCentrosCusto()
      .then((data) => {
        if (active) setCentrosCusto(data);
      })
      .catch((err) => {
        if (active) setCentrosCustoError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível carregar os centros de custo."));
      })
      .finally(() => {
        if (active) setCentrosCustoLoading(false);
      });

    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!hasSearched) return undefined;
    if (skipFetchForView.current === view) {
      skipFetchForView.current = null;
      return undefined;
    }

    let active = true;
    setLoading(true);
    setHasLoadedResponse(false);
    setError("");
    setCanRetry(false);

    const requestParams = { ...appliedFilters, page, size: pageSize };
    const request = isParcelasView
      ? cobrancaListagensService.getParcelas(requestParams)
      : cobrancaListagensService.getTitulos(requestParams);

    request
      .then((data) => {
        if (active) {
          setResult(data);
          setHasLoadedResponse(true);
        }
      })
      .catch((err) => {
        if (!active) return;
        setResult(EMPTY_PAGE);
        setError(cobrancaListagensService.getApiErrorMessage(
          err,
          `Não foi possível carregar ${isParcelasView ? "as parcelas" : "os títulos"}.`,
        ));
        setCanRetry(true);
        setHasLoadedResponse(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [appliedFilters, hasSearched, isParcelasView, page, pageSize, view]);

  const getCurrentViewSnapshot = () => ({
    draftFilters,
    appliedFilters,
    page,
    pageSize,
    result,
    hasSearched,
    hasLoadedResponse,
    error,
    canRetry,
  });

  const handleViewChange = (_, nextView) => {
    if (!nextView || nextView === view) return;
    viewCache.current[view] = getCurrentViewSnapshot();
    const nextState = viewCache.current[nextView];
    skipFetchForView.current = nextState.hasLoadedResponse ? nextView : null;
    setView(nextView);
    setDraftFilters(nextState.draftFilters);
    setAppliedFilters(nextState.appliedFilters);
    setPage(nextState.page);
    setPageSize(nextState.pageSize);
    setResult(nextState.result);
    setError(nextState.error);
    setCanRetry(nextState.canRetry);
    setHasSearched(nextState.hasSearched);
    setHasLoadedResponse(nextState.hasLoadedResponse);
    setLoading(nextState.hasSearched && !nextState.hasLoadedResponse);
    setSelectedParcel(null);
  };

  const handleOpenTitleParcels = (title) => {
    if (title.companyId === null || title.companyId === undefined || title.receivableBillId === null || title.receivableBillId === undefined) {
      setError("Não foi possível abrir as parcelas porque o título não possui todos os identificadores necessários.");
      setCanRetry(false);
      return;
    }

    viewCache.current.titulos = getCurrentViewSnapshot();
    const parcelasFilters = {
      ...EMPTY_PARCELAS_FILTERS,
      companyId: String(title.companyId),
      receivableBillId: String(title.receivableBillId),
    };
    const nextParcelState = {
      ...viewCache.current.parcelas,
      draftFilters: parcelasFilters,
      appliedFilters: parcelasFilters,
      page: 0,
      result: EMPTY_PAGE,
      hasSearched: true,
      hasLoadedResponse: false,
      error: "",
      canRetry: false,
    };
    viewCache.current.parcelas = nextParcelState;
    skipFetchForView.current = null;
    setView("parcelas");
    setDraftFilters(nextParcelState.draftFilters);
    setAppliedFilters(nextParcelState.appliedFilters);
    setPage(0);
    setPageSize(nextParcelState.pageSize);
    setResult(EMPTY_PAGE);
    setError("");
    setCanRetry(false);
    setHasSearched(true);
    setHasLoadedResponse(false);
    setLoading(true);
    setSelectedParcel(null);
  };

  const handleFilterChange = (name, value) => {
    setDraftFilters((current) => ({ ...current, [name]: value }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    if (
      isParcelasView &&
      draftFilters.dueDateInicio &&
      draftFilters.dueDateFim &&
      draftFilters.dueDateInicio > draftFilters.dueDateFim
    ) {
      setError("A data inicial de vencimento deve ser anterior ou igual à data final.");
      setCanRetry(false);
      return;
    }
    if (
      isParcelasView &&
      draftFilters.diasAtrasoMin &&
      draftFilters.diasAtrasoMax &&
      Number(draftFilters.diasAtrasoMin) > Number(draftFilters.diasAtrasoMax)
    ) {
      setError("O atraso mínimo não pode ser maior que o atraso máximo.");
      setCanRetry(false);
      return;
    }

    setError("");
    setCanRetry(false);
    setPage(0);
    setAppliedFilters({ ...draftFilters });
    setHasLoadedResponse(false);
    setHasSearched(true);
  };

  const handleClearFilters = () => {
    const emptyFilters = isParcelasView ? EMPTY_PARCELAS_FILTERS : EMPTY_TITULOS_FILTERS;
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setError("");
    setCanRetry(false);
    setPage(0);
    setResult(EMPTY_PAGE);
    setHasSearched(false);
    setHasLoadedResponse(false);
    setLoading(false);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(0);
  };

  return (
    <Stack spacing={1.25}>
      <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.75 }, borderRadius: 2, bgcolor: "var(--ops-surface)", border: "1px solid var(--ops-border)" }}>
        <Tabs value={view} onChange={handleViewChange} aria-label="Visão de títulos ou parcelas" variant="scrollable" sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, px: 2, textTransform: "none", fontWeight: 750 } }}>
          <Tab value="titulos" label="Títulos" />
          <Tab value="parcelas" label="Parcelas" />
        </Tabs>
        <Divider sx={{ borderColor: "var(--ops-border)", mb: 1.5 }} />

        <Box component="form" onSubmit={handleApplyFilters}>
          <Stack spacing={1.25}>
            <Typography sx={{ color: "var(--ops-heading)", fontSize: 14, fontWeight: 800 }}>
              Filtros de {isParcelasView ? "parcelas" : "títulos"}
            </Typography>
            <FilterFields
              fields={fields}
              values={draftFilters}
              onChange={handleFilterChange}
              centrosCusto={centrosCusto}
              centrosCustoLoading={centrosCustoLoading}
              centrosCustoError={centrosCustoError}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button type="button" onClick={handleClearFilters} sx={{ textTransform: "none" }}>
                Limpar filtros
              </Button>
              <Button type="submit" variant="contained" startIcon={<SearchRounded />} sx={{ textTransform: "none", fontWeight: 750 }}>
                Aplicar filtros
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {error ? (
        <Alert
          severity="error"
          action={canRetry ? <Button color="inherit" size="small" onClick={() => { setError(""); setAppliedFilters({ ...appliedFilters }); }}>Tentar novamente</Button> : undefined}
          sx={{ borderRadius: 1.5 }}
        >
          {error}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ borderRadius: 2, bgcolor: "var(--ops-surface)", border: "1px solid var(--ops-border)", overflow: "hidden" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: { xs: 1.25, md: 1.75 }, py: 1.25 }}>
          <Typography sx={{ color: "var(--ops-heading)", fontSize: 14, fontWeight: 800 }}>
            {isParcelasView ? "Parcelas inadimplentes" : "Títulos inadimplentes"}
          </Typography>
          {hasSearched ? <Chip size="small" label={`${formatNumber(result.totalElements)} registros`} sx={{ color: "var(--ops-text)", bgcolor: "var(--ops-subtle)", fontWeight: 700 }} /> : null}
        </Stack>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ minHeight: 220, color: "var(--ops-muted)" }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: 13 }}>Carregando {isParcelasView ? "parcelas" : "títulos"}...</Typography>
          </Stack>
        ) : !hasSearched ? (
          <Box sx={{ minHeight: 180, display: "grid", placeItems: "center", px: 2, borderTop: "1px solid var(--ops-border)" }}>
            <Typography sx={{ color: "var(--ops-muted)", fontSize: 13, textAlign: "center" }}>
              Use os filtros acima e clique em “Aplicar filtros” para consultar {isParcelasView ? "as parcelas" : "os títulos"}.
            </Typography>
          </Box>
        ) : result.content.length === 0 ? (
          <Box sx={{ minHeight: 180, display: "grid", placeItems: "center", px: 2, borderTop: "1px solid var(--ops-border)" }}>
            <Typography sx={{ color: "var(--ops-muted)", fontSize: 13, textAlign: "center" }}>
              {error ? "Não foi possível consultar os dados." : `Nenhum ${isParcelasView ? "parcela" : "título"} encontrado para os filtros informados.`}
            </Typography>
          </Box>
        ) : isParcelasView ? (
          <ParcelasTable rows={result.content} onSelect={setSelectedParcel} />
        ) : (
          <TitulosTable rows={result.content} onSelect={handleOpenTitleParcels} />
        )}

        {hasSearched ? (
          <ListPagination
            page={page}
            pageSize={pageSize}
            totalElements={result.totalElements}
            loading={loading}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onPageSizeChange={handlePageSizeChange}
          />
        ) : null}
      </Paper>

      <ParcelDetail parcel={selectedParcel} onClose={() => setSelectedParcel(null)} />
    </Stack>
  );
}

function TitulosTable({ rows, onSelect }) {
  return (
    <TableContainer sx={{ borderTop: "1px solid var(--ops-border)" }}>
      <Table size="small" sx={{ minWidth: 1120 }} aria-label="Títulos inadimplentes">
        <TableHead>
          <TableRow>
            <TableCell>Empresa / cliente</TableCell>
            <TableCell>Documento</TableCell>
            <TableCell>Título / parcelas</TableCell>
            <TableCell>Emissão</TableCell>
            <TableCell>Centro de custo</TableCell>
            <TableCell>Unidade</TableCell>
            <TableCell align="right">Valor do título</TableCell>
            <TableCell align="right">Parcelas</TableCell>
            <TableCell align="right">Valor inadimplente</TableCell>
            <TableCell align="right">Maior atraso</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((title) => (
            <TableRow key={`${title.companyId}-${title.receivableBillId}`} hover>
              <TableCell>
                <Typography sx={{ color: "var(--ops-heading)", fontWeight: 750, fontSize: 12.5 }}>{title.clientName || "—"}</Typography>
                <Typography sx={{ color: "var(--ops-muted)", fontSize: 11.5 }}>Empresa {title.companyId ?? "—"} · Cliente {title.clientId ?? "—"}</Typography>
              </TableCell>
              <TableCell>{title.documentNumber || "—"}</TableCell>
              <TableCell>
                <Stack spacing={0.25} alignItems="flex-start">
                  <Typography sx={{ color: "var(--ops-text)", fontSize: 12 }}>{title.receivableBillId ?? "—"}</Typography>
                  <Button
                    size="small"
                    startIcon={<VisibilityOutlined />}
                    onClick={() => onSelect(title)}
                    disabled={title.companyId == null || title.receivableBillId == null}
                    aria-label={`Ver parcelas do título ${title.receivableBillId ?? ""}`}
                    sx={{ minWidth: 0, px: 0.5, textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Ver parcelas
                  </Button>
                </Stack>
              </TableCell>
              <TableCell>{formatDate(title.issueDate)}</TableCell>
              <TableCell>{title.costCentersId || "—"}</TableCell>
              <TableCell>{title.units || "—"}</TableCell>
              <TableCell align="right">{formatNullableCurrency(title.receivableBillValue)}</TableCell>
              <TableCell align="right">{formatNullableNumber(title.parcelasInadimplentes)}</TableCell>
              <TableCell align="right" sx={{ color: "var(--ops-danger)", fontWeight: 800 }}>{formatNullableCurrency(title.valorInadimplente)}</TableCell>
              <TableCell align="right">{title.maiorAtraso === null || title.maiorAtraso === undefined ? "—" : formatDays(title.maiorAtraso)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ParcelasTable({ rows, onSelect }) {
  return (
    <TableContainer sx={{ borderTop: "1px solid var(--ops-border)" }}>
      <Table size="small" sx={{ minWidth: 1120 }} aria-label="Parcelas inadimplentes">
        <TableHead>
          <TableRow>
            <TableCell>Cliente</TableCell>
            <TableCell>Documento / título</TableCell>
            <TableCell>Parcela</TableCell>
            <TableCell>Vencimento</TableCell>
            <TableCell align="right">Atraso</TableCell>
            <TableCell align="right">Valor sem acréscimos</TableCell>
            <TableCell align="right">Acréscimos</TableCell>
            <TableCell align="right">Valor corrigido</TableCell>
            <TableCell>SPC/Serasa</TableCell>
            <TableCell align="right">Detalhes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((parcel) => (
            <TableRow key={`${parcel.companyId}-${parcel.receivableBillId}-${parcel.installmentId}`} hover>
              <TableCell>
                <Typography sx={{ color: "var(--ops-heading)", fontWeight: 750, fontSize: 12.5 }}>{parcel.clientName || "—"}</Typography>
                <Typography sx={{ color: "var(--ops-muted)", fontSize: 11.5 }}>Cliente {parcel.clientId ?? "—"} · Empresa {parcel.companyId ?? "—"}</Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ color: "var(--ops-text)", fontSize: 12 }}>{parcel.documentNumber || "—"}</Typography>
                <Typography sx={{ color: "var(--ops-muted)", fontSize: 11.5 }}>Título {parcel.receivableBillId ?? "—"}</Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ color: "var(--ops-text)", fontSize: 12 }}>{parcel.installmentNumber || parcel.installmentId || "—"}</Typography>
                <Typography sx={{ color: "var(--ops-muted)", fontSize: 11.5 }}>{parcel.conditionType || "Sem condition type"}</Typography>
              </TableCell>
              <TableCell>{formatDate(parcel.dueDate)}</TableCell>
              <TableCell align="right" sx={{ color: "var(--ops-danger)", fontWeight: 800 }}>{parcel.daysOfDelay === null || parcel.daysOfDelay === undefined ? "—" : formatDays(parcel.daysOfDelay)}</TableCell>
              <TableCell align="right">{formatNullableCurrency(parcel.correctedValueWithoutAdditions)}</TableCell>
              <TableCell align="right">{formatNullableCurrency(parcel.totalAdditions)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>{formatNullableCurrency(parcel.correctedValueWithAdditions)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={parcel.installmentSentToSPCSerasa ? "Enviada" : "Não enviada"}
                  sx={{ bgcolor: parcel.installmentSentToSPCSerasa ? "var(--ops-warning-bg)" : "var(--ops-subtle)", color: parcel.installmentSentToSPCSerasa ? "var(--ops-warning)" : "var(--ops-muted)", fontWeight: 700 }}
                />
              </TableCell>
              <TableCell align="right">
                <Button size="small" startIcon={<VisibilityOutlined />} onClick={() => onSelect(parcel)} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
                  Ver
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
